/**
* JAVASCRIPT DEVELOPER DOCUMENTATION
*
* Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
* using Web Components, it offers a highly modular architecture that makes customization and
* maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
* store runs as fast as possible to provide your customers with a seamless shopping experience.
*
* If you would like to add your own JS to Enterprise, we recommend using this file and referencing
* it using Theme Settings > Advanced > Custom HTML.
*
* As a brief overview, Enterprise:
*  - Broadcasts many JS events.
*  - Is built using Web Components.
*  - Follows a 'code splitting' architecture.
*  - Is completely custom built (no JS libraries other than instant.page)
*  - Has a number of JS utilities.
*
*
*
* =================================================================================================
* Custom JavaScript Events
* =================================================================================================
*
* Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
*
* When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
* console everytime it is triggered.
*
* Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
* 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
*
* All 'Return data' detailed in this section is returned within the 'event.detail' object.
*
* The available events are:
*  1.  on:variant:change
*  2.  on:cart:add
*  3.  on:cart:error
*  4.  on:line-item:change
*  5.  on:cart-drawer:before-open
*  6.  on:cart-drawer:after-open
*  7.  on:cart-drawer:after-close
*  8.  on:quickbuy:before-open
*  9.  on:quickbuy:after-open
*  10. on:quickbuy:after-close
*  11. dispatch:cart-drawer:open
*  12. dispatch:cart-drawer:refresh
*  13. dispatch:cart-drawer:close
*  14. on:debounced-resize
*  15. on:breakpoint-change
*
* -------------------------------------------------------------------------------------------------
* 1) on:variant:change
* -------------------------------------------------------------------------------------------------
* Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
*
* How to listen:
* document.addEventListener('on:variant:change', (event) => {
*  // your code here
* });
*
* Returned data:
*  - form: the product form content
*  - variant: the selected variant object
*  - product: the product object (includes a list of all variants)
*
*
* -------------------------------------------------------------------------------------------------
* 2) on:cart:add
* -------------------------------------------------------------------------------------------------
* Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
* event does not fire when the added variant was already in the cart. To listen for this, see the
* on:line-item:change event.
*
* How to listen:
* document.addEventListener('on:cart:add', (event) => {
*   // your code here
* });
*
* Returned data:
*   - cart: the new cart object after the variant was added
*   - variantId: id of the variant that was just added to the cart
*
*
* -------------------------------------------------------------------------------------------------
* 3) on:line-item:change
* -------------------------------------------------------------------------------------------------
* Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
* 0, this indicates the item was removed from that cart.
*
* Note, when adding a variant to the cart - this event will fire if that variant is already in the
* cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
*
* How to listen:
* document.addEventListener('on:line-item:change', (event) => {
*   // your code here
* });
*
* Returned data:
*   - cart: the new cart object after the quantity change was completed
*   - variantId: id of the variant that was just updated
*   - newQuantity: new quantity of the line item
*   - oldQuantity: old quantity of the line item
*
*
* -------------------------------------------------------------------------------------------------
* 4) on:cart:error
* -------------------------------------------------------------------------------------------------
* Fires when an action related to the cart has failed, for example adding too much quantity of an
* item to the cart.
*
* How to listen:
* document.addEventListener('on:cart:error', (event) => {
*   // your code here
* });
*
* Returned data:
*   - error: the error message
*
*
* -------------------------------------------------------------------------------------------------
* 5) on:cart-drawer:before-open
* -------------------------------------------------------------------------------------------------
* Fires before the cart drawer opens.
*
* How to listen:
* document.addEventListener('on:cart-drawer:before-open', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 6) on:cart-drawer:after-open
* -------------------------------------------------------------------------------------------------
* Fires after the cart drawer has finished opening.
*
* How to listen:
* document.addEventListener('on:cart-drawer:after-open', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 7) on:cart-drawer:after-close
* -------------------------------------------------------------------------------------------------
* Fires after the cart drawer has finished closing.
*
* How to listen:
* document.addEventListener('on:cart-drawer:after-close', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 8) on:quickbuy:before-open
* -------------------------------------------------------------------------------------------------
* Fires before the quick buy drawer opens.
*
* How to listen:
* document.addEventListener('on:quickbuy:before-open', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 9) on:quickbuy:after-open
* -------------------------------------------------------------------------------------------------
* Fires after the quick buy drawer has finished opening.
*
* How to listen:
* document.addEventListener('on:quickbuy:after-open', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 10) on:quickbuy:after-close
* -------------------------------------------------------------------------------------------------
* Fires after the quick buy drawer has finished closing.
*
* How to listen:
* document.addEventListener('on:quickbuy:after-close', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 11) dispatch:cart-drawer:open
* -------------------------------------------------------------------------------------------------
* Opens the cart drawer (if enabled in the Theme Settings).
*
* How to trigger:
* document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
*
* You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
* element that should be focussed on when the drawer is closed.
*
* Example:
* document.getElementById('header-search').addEventListener('keydown', (evt) => {
*   if (evt.keyCode === 67) {
*     evt.preventDefault();
*     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
*       detail: {
*         opener: evt.target
*       }
*     }));
*   }
* });
*
* In this example, we attach a keydown listener to the search input in the header. If the user
* presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
* the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
* specifies the search input as the opener. When the cart drawer is closed, focus is returned to
* the search input.
*
*
* -------------------------------------------------------------------------------------------------
* 12) dispatch:cart-drawer:refresh
* -------------------------------------------------------------------------------------------------
* Refreshes the contents of the cart drawer.
*
* This event is useful when you are adding variants to the cart and would like to instruct the
* theme to re-render the cart drawer.
*
* How to trigger:
* document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
*   bubbles: true
* }));
*
*
* -------------------------------------------------------------------------------------------------
* 13) dispatch:cart-drawer:close
* -------------------------------------------------------------------------------------------------
* Closes the cart drawer.
*
* How to trigger:
* document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
*
*
* -------------------------------------------------------------------------------------------------
* 14) on:debounced-resize
* -------------------------------------------------------------------------------------------------
* Fires when the viewport finishes resizing (debounced to 300ms by default).
*
* How to listen:
* window.addEventListener('on:debounced-resize', (event) => {
*   // your code here
* });
*
*
* -------------------------------------------------------------------------------------------------
* 15) on:breakpoint-change
* -------------------------------------------------------------------------------------------------
* Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
* for more.
*
* Example:
* window.addEventListener('on:breakpoint-change', (event) => {
*  if (theme.mediaMatches.md) {
*   console.log('we are not on mobile');
*  }
* });
*
*
*
* =================================================================================================
* Web Components
* =================================================================================================
*
* Enterprise utilizes Web Components to the fullest.
*
* Web Components are a set of standardized APIs that allow developers to create custom, reusable
* HTML elements that can be used across different web pages and applications.
* Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
* Templates.
*
* See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
*
*
*
* =================================================================================================
* Third-Party JavaScript Dependencies
* =================================================================================================
*
* Enterprise only has one third-party dependency: instant.page (https://instant.page/).
*
* It's included locally and is only active if it has been enabled in
* 'Theme Settings > Advanced > Preload links on hover'.
*
* Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
* customer hovers over them.
*
*
*
* =================================================================================================
* Code Splitting
* =================================================================================================
* We followed the ‘code splitting’ technique when building Enterprise.
*
* Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
* small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
* performance of our theme by reducing the amount of code that needs to be loaded upfront.
*
* If the customer is visiting a specific page of the theme that requires certain JavaScript
* functionality, only the code needed for that page will be loaded, rather than one large
* JavaScript file containing largely unused code. For example, the file media-gallery.js will
* only be loaded if there is a media gallery on the page.
*
* Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
* HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
* connection at the same time - meaning multiple JS files are essentially served at the speed of a
* single file.
*
* The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
* utility JS which is likely to be needed by many scripts. This is outlined more in the next
* section.
*
*
*
* =================================================================================================
* Utilities
* =================================================================================================
* Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
* outlined below. See main.js for more.
*
*
* -------------------------------------------------------------------------------------------------
* 1) Lazy Loading
* -------------------------------------------------------------------------------------------------
* Lazy loading is a technique for delaying the loading of certain elements until they are needed,
* which can help improve page load times.
*
* We use three functions used for lazy loading images and scripts in our theme:
*
*  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
*    images to their src and srcset attributes.
*  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
*  - initLazyScript function - only loads a script when a specific element is scrolled into view.
*
*
* -------------------------------------------------------------------------------------------------
* 2) Cookies
* -------------------------------------------------------------------------------------------------
* Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
* tracking user activity, remembering user preferences or other similar purposes.
*
* We use three functions to manage cookies:
*
*  - setCookie function - sets a cookie with a given name, value and number of days until it
*  should expire.
*  - getCookie function - takes in the name of a cookie to retrieve its value.
*  - deleteCookie function - takes in the name of a cookie to delete it.
*
*
* -------------------------------------------------------------------------------------------------
* 3) Media Queries
* -------------------------------------------------------------------------------------------------
* The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
* specified in our theme, and adds listeners for each media query.
*
* These are:
*
* mediaQueries: {
*  sm: '(min-width: 600px)',
*  md: '(min-width: 769px)',
*  lg: '(min-width: 1024px)',
*  xl: '(min-width: 1280px)',
*  xxl: '(min-width: 1536px)',
*  portrait: '(orientation: portrait)'
* }
*
* If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
* event is dispatched.
*
* You can request the entire theme.mediaMatches object to check which media queries are currently
* matched. In this case, the returned data will be an object with the names of the media queries as
* keys, and boolean values indicating whether they are currently matched or not.
*
* Example:
*
* {
*  sm: true,
*  md: true,
*  lg: true,
*  xl: true,
*  xxl: false,
*  portrait: false
* }
*
* You can reference a specific media query to check if it's currently matched by using:
* theme.mediaMatches.lg.
*
* To check if you're on mobile you can use:
* !theme.mediaMatches.md
*
* If you want to perform some action when the breakpoint changes, you can listen for the
* breakpoint-change event on the window object.
*
* Example:
* window.addEventListener('on:breakpoint-change', (event) => {
*  // your code here
* });
*
* =================================================================================================
*
* Have fun! - The Clean Canvas Development Team.
*/

/* =================================================================================================
 * AlumierMD × RecurPay visibility gate
 *
 * Single source of truth for whether the "Subscribe & Save" (RecurPay) widget may be shown on an
 * AlumierMD PDP. Requirement: the RecurPay widget only displays when the add-to-cart button
 * displays — i.e. only for `patient_owned` and `fail_open`.
 *
 * RecurPay's own third-party script initialises the widget AFTER our verification gate has run and
 * writes its own inline `display` style, which beats both our earlier inline write and any CSS
 * guard. So the gate has to be self-healing: it records the desired state and re-asserts it via a
 * scoped MutationObserver whenever something changes the widget while it is not allowed.
 *
 * Fails CLOSED on `body.product-alumiermd`: unknown/initial state == hidden, so an unverified
 * visitor can never be shown a subscribe option. Non-AlumierMD PDPs are untouched — every code path
 * below no-ops unless the body carries `product-alumiermd`.
 * ================================================================================================= */
(function () {
  'use strict';

  var WIDGET_SELECTOR = '.recurpay-pdp-widget';
  var TOGGLE_SELECTOR = '.recurpay-toggle';
  var LINK_SELECTOR = '.subscription-info-link';
  var GATED_SELECTOR = WIDGET_SELECTOR + ', ' + TOGGLE_SELECTOR + ', ' + LINK_SELECTOR;
  var observers = [];
  var observedNodes = [];
  var writing = false; // re-entrancy guard: ignore mutations caused by our own writes

  // AlumierMD flow switch (Theme settings > AlumierMD). window.SKQ_ALUMIER_FLOW is set from Liquid in
  // layout/theme.liquid before this script loads. Only 'product_page' (OLD flow) enables this gate; in the
  // default 'checkout' flow isAlumierPdp() is always false, isAllowed() is always true and every function below
  // is a no-op, so RecurPay "Subscribe & Save" shows on AlumierMD PDPs like any other brand. (layout/theme.liquid
  // also only emits body.product-alumiermd in the old flow.)
  var GATE_ENABLED = window.SKQ_ALUMIER_FLOW === 'product_page';

  function isAlumierPdp() {
    return GATE_ENABLED && !!(document.body && document.body.classList.contains('product-alumiermd'));
  }

  /**
   * The cart drawer is global — it exists on AlumierMD PDPs too, and the subscription info link is
   * injected into it for whatever is actually in the cart. Those instances have nothing to do with
   * the AlumierMD product being viewed, so the gate must never touch them. Mirrors the injector's
   * own PDP-vs-drawer test.
   */
  function isInCartDrawer(el) {
    return !!(el.closest('.drawer--cart') || el.closest('cart-drawer'));
  }

  /** Elements this gate is allowed to control: PDP-context only, never cart-drawer instances. */
  function gatedElements() {
    var all = document.querySelectorAll(GATED_SELECTOR);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (!isInCartDrawer(all[i])) out.push(all[i]);
    }
    return out;
  }

  // Default FALSE — fail closed. Only showAlumierState() may flip this to true.
  if (typeof window.__alumierRecurpayAllowed !== 'boolean') {
    window.__alumierRecurpayAllowed = false;
  }

  /** Desired visibility. Non-AlumierMD pages are always "allowed" (i.e. not our concern). */
  function isAllowed() {
    if (!isAlumierPdp()) return true;
    return window.__alumierRecurpayAllowed === true;
  }

  function applyTo(el, allowed) {
    if (!el) return;
    var display = el.style.getPropertyValue('display');
    var priority = el.style.getPropertyPriority('display');

    if (allowed) {
      // Only the widget needs an explicit inline value on the way back, to beat the theme.liquid
      // FOUC guard (`body.product-alumiermd .recurpay-pdp-widget { display: none }`). The toggle
      // (`display: flex`) and the info link (`display: inline-flex !important`) carry their own
      // display in main.css — forcing `block` on them would break their layout, so we just clear
      // our inline value and let the stylesheet win.
      var restore = el.matches(WIDGET_SELECTOR) ? 'block' : '';

      // Already correct — skip the write. Writing an identical value still emits a mutation
      // record, which would bounce us straight back in here forever.
      if (display === restore && !priority) {
        if (el.hasAttribute('data-alumier-gated')) el.removeAttribute('data-alumier-gated');
        return;
      }
      if (restore) el.style.setProperty('display', restore);
      else el.style.removeProperty('display');
      el.removeAttribute('data-alumier-gated');
      return;
    }

    if (display === 'none' && priority === 'important') {
      if (el.getAttribute('data-alumier-gated') !== 'hidden') el.setAttribute('data-alumier-gated', 'hidden');
      return;
    }
    // `!important` inline is the only thing RecurPay's late inline `display:block` cannot beat.
    el.style.setProperty('display', 'none', 'important');
    el.setAttribute('data-alumier-gated', 'hidden');
  }

  /**
   * Apply the desired state to every RecurPay widget on the page plus any injected toggle and the
   * "How do subscriptions work?" info link. Safe to call as often as you like; it is a no-op off
   * AlumierMD PDPs and never touches cart-drawer instances.
   */
  function enforceRecurpayVisibility() {
    if (!isAlumierPdp()) return;
    if (writing) return;
    writing = true;
    try {
      var allowed = isAllowed();
      var els = gatedElements();
      for (var i = 0; i < els.length; i++) applyTo(els[i], allowed);
    } finally {
      writing = false;
    }
  }

  /** Record the desired state and immediately re-assert it. */
  function setRecurpayAllowed(allowed) {
    window.__alumierRecurpayAllowed = allowed === true;
    enforceRecurpayVisibility();
  }

  function onMutation() {
    if (writing) return;
    enforceRecurpayVisibility();
  }

  /**
   * Watch each gated element and its immediate parent only — cheap and scoped. Observing the
   * parents means a LATE INJECTION of the info link (or the widget) into that container fires a
   * childList record, so a newly injected element gets gated before it can paint. We never observe
   * document.body's whole subtree.
   */
  function observeTargets() {
    if (!isAlumierPdp()) return;
    var els = gatedElements();
    for (var i = 0; i < els.length; i++) {
      var targets = [els[i], els[i].parentElement];
      for (var j = 0; j < targets.length; j++) {
        var node = targets[j];
        if (!node || observedNodes.indexOf(node) !== -1) continue;
        // Never observe inside the cart drawer — it is out of the gate's scope entirely.
        if (node.nodeType === 1 && isInCartDrawer(node)) continue;
        var mo = new MutationObserver(onMutation);
        mo.observe(node, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true
        });
        observers.push(mo);
        observedNodes.push(node);
      }
    }
  }

  function start() {
    if (!isAlumierPdp()) return;
    enforceRecurpayVisibility();
    observeTargets();
    // RecurPay injects its widget late; re-scan for a short window so newly created widgets get
    // both gated and observed. Bounded — no permanent polling.
    var scans = 0;
    var timer = setInterval(function () {
      scans += 1;
      observeTargets();
      enforceRecurpayVisibility();
      if (scans >= 20) clearInterval(timer); // ~10s at 500ms
    }, 500);
  }

  // Re-assert whenever the verification state changes.
  document.addEventListener('alumier:state', enforceRecurpayVisibility);

  window.__alumierRecurpay = {
    enforce: enforceRecurpayVisibility,
    setAllowed: setRecurpayAllowed,
    isAllowed: isAllowed,
    observe: observeTargets
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());

/* =================================================================================================
 * AlumierMD Verification Flow
 * Handles: API check, inline state blocks, graceful degradation, Klaviyo events, alerting
 * ================================================================================================= */
document.addEventListener("DOMContentLoaded", function () {
  var checkemail = document.getElementById('alumier-customer-id').value;
  var customer_fname = document.getElementById('alumier-customer-fname').value;
  var customer_lname = document.getElementById('alumier-customer-lname').value;

  var HEROKU_URL = 'https://skintique-clinic-27e2f7a5cbf2.herokuapp.com';
  var CHECK_EMAIL_URL = HEROKU_URL + '/check_email.php';
  var PROXY_URL = 'https://alumier-proxy.skintique.co.uk';
  var API_TIMEOUT_MS = 10000;
  var CACHE_KEY = 'alumier_verification_' + (checkemail || 'anon');
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (patient_owned is stable)
  var shopifyCustomerId = document.getElementById('alumier-shopify-customer-id') ? document.getElementById('alumier-shopify-customer-id').value : '';

  // AlumierMD flow switch (Theme settings > AlumierMD; window.SKQ_ALUMIER_FLOW is set from Liquid in
  // layout/theme.liquid). This flag controls the PDP / card / recommendation verification gate below
  // (checkAlumierEmail, showAlumierState, hideButtons/applyCardButtonState).
  //   'product_page' (OLD flow) -> true: the gate runs exactly as before.
  //   'checkout' (default, NEW)  -> false: AlumierMD is bought like any other brand before checkout - no Heroku
  //   check on page load, no add-to-cart hiding, no 'alumier:state' event, no cart "api-failed" attribute;
  //   AlumierMD customers are verified at Checkout instead (Part 2 pop-up).
  // Everything else in this handler (header login link, register flow, savedRegisterUrl redirects,
  // registerAlumier) is unaffected by the flag.
  var ALUMIER_ATC_GATE_ENABLED = window.SKQ_ALUMIER_FLOW === 'product_page';

  // Clean up old non-email-specific cache key
  try { sessionStorage.removeItem('alumier_verification'); } catch(e) {}

  /* ----------------------------- Utility: fetch with AbortController timeout ----------------------------- */
  function fetchWithTimeout(url, options, timeoutMs) {
    var controller = new AbortController();
    var timeoutId = setTimeout(function() { controller.abort(); }, timeoutMs || API_TIMEOUT_MS);
    var opts = Object.assign({}, options, { signal: controller.signal });
    return fetch(url, opts).finally(function() { clearTimeout(timeoutId); });
  }

  /* ----------------------------- Utility: cookie helpers ----------------------------- */
  function getCookie(name) {
    var cookies = document.cookie.split('; ');
    for (var i = 0; i < cookies.length; i++) {
      var parts = cookies[i].split('=');
      if (parts[0] === name) return decodeURIComponent(parts[1]);
    }
    return null;
  }

  /* ----------------------------- Utility: sessionStorage cache ----------------------------- */
  function getCachedResult() {
    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      var parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function setCachedResult(reasonCode, data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        reason_code: reasonCode,
        data: data,
        timestamp: Date.now()
      }));
    } catch (e) { /* storage full, ignore */ }
  }

  /* ----------------------------- Utility: alert admin on API failure ----------------------------- */
  function alertAdminOnFailure(errorMsg) {
    try {
      var lastSent = parseInt(localStorage.getItem('alumier_alert_last_sent') || '0', 10);
      if (Date.now() - lastSent < 3600000) return; // max 1 per hour
      localStorage.setItem('alumier_alert_last_sent', String(Date.now()));
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          form_type: 'contact',
          utf8: '\u2713',
          'contact[email]': 'hello@skintique.co.uk',
          'contact[name]': 'AlumierMD API Monitor',
          'contact[body]': 'AlumierMD API failure at ' + new Date().toISOString() + ' on ' + window.location.href + '. Error: ' + errorMsg
        }).toString()
      }).catch(function() {});
    } catch (e) { /* best-effort */ }
  }

  /* ----------------------------- Utility: fire Klaviyo event ----------------------------- */
  function trackKlaviyoEvent(eventName, props) {
    if (window._learnq) {
      window._learnq.push(['track', eventName, props]);
    }
  }

  /* ----------------------------- Register customer on AlumierMD ----------------------------- */
  function registerAlumier() {
    var email = document.getElementById('alumier-customer-id').value;
    var fname = document.getElementById('alumier-customer-fname').value;
    var lname = document.getElementById('alumier-customer-lname').value;
    if (!email) return;

    fetchWithTimeout(HEROKU_URL + '/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, first_name: fname, last_name: lname })
    }, API_TIMEOUT_MS)
    .then(function(r) { return r.json(); })
    .then(function(result) {
      if (result.error) {
        console.error('AlumierMD register error:', result.error);
      } else {
        console.log('AlumierMD: user registered successfully');
      }
    })
    .catch(function(err) { console.error('AlumierMD register failed:', err); });
  }

  /* ----------------------------- Auto-register and re-verify ----------------------------- */
  function registerAlumierAndVerify() {
    var email = document.getElementById('alumier-customer-id') ? document.getElementById('alumier-customer-id').value : '';
    var fname = document.getElementById('alumier-customer-fname') ? document.getElementById('alumier-customer-fname').value : '';
    var lname = document.getElementById('alumier-customer-lname') ? document.getElementById('alumier-customer-lname').value : '';

    if (!email) {
      // No email — can't register, show not-found state
      showAlumierState('patient_not_found');
      return;
    }

    console.log('AlumierMD: patient not found — auto-registering...');

    fetchWithTimeout(HEROKU_URL + '/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, first_name: fname, last_name: lname })
    }, API_TIMEOUT_MS)
    .then(function(r) { return r.json(); })
    .then(function(result) {
      if (result.error) {
        console.error('AlumierMD auto-register error:', result.error);
        // Registration failed — show not-found as fallback
        showAlumierState('patient_not_found');
        return;
      }

      console.log('AlumierMD: auto-registration successful, re-verifying...');

      // Re-verify after registration to confirm patient_owned status
      fetchWithTimeout(CHECK_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      }, API_TIMEOUT_MS)
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function(verifyData) {
        console.log('AlumierMD re-verification after register:', verifyData);
        if (verifyData.verification_response &&
            verifyData.verification_response.reason_code === 'patient_owned') {
          // Cache the successful result
          setCachedResult(verifyData.verification_response);
          showAlumierState('patient_owned');
        } else if (verifyData.verification_response &&
                   verifyData.verification_response.reason_code === 'patient_not_owned') {
          // Registered but somehow owned by another clinic
          showAlumierState('patient_not_owned');
        } else {
          // Re-verification didn't return owned — trust the registration and show verified
          // The API may have a slight delay in reflecting the registration
          showAlumierState('patient_owned');
        }
      })
      .catch(function(err) {
        console.error('AlumierMD re-verify failed:', err);
        // Registration succeeded, re-verify failed — trust registration, show verified
        showAlumierState('patient_owned');
      });
    })
    .catch(function(err) {
      console.error('AlumierMD auto-register failed:', err);
      alertAdminOnFailure('Auto-register failed: ' + (err.message || 'Unknown error'));
      // Registration call failed — fail open, allow purchase
      showAlumierState('fail_open');
    });
  }

  /* ----------------------------- Request transfer from AlumierMD (customer-initiated) -----------------------------
   *
   * GDPR-compliant flow (AlumierMD will not accept emails sent on the customer's
   * behalf — the request must originate from the patient's own email address):
   *
   *   1. POST /api/draft-order to create a Shopify draft order tagged
   *      `alumier-pending-transfer` with the current PDP variant as the first
   *      line item. This is the state record the cron + inbound webhook both
   *      scan for. Same endpoint the "Add to My Order" button uses — the proxy
   *      handles both create-new and append-existing cases via
   *      `findPendingDraftOrder`.
   *
   *   2. Read the returned `draftOrderId`. This becomes the `Ref:` in the mailto
   *      subject line so the inbound webhook can correlate replies back to
   *      the specific draft order when AlumierMD CS hits Reply All.
   *
   *   3. Open a mailto:... URL that pre-fills the customer's mail client with
   *      a formatted transfer request. The customer hits Send themselves. The
   *      CC list includes our tracking address so the Resend inbound webhook
   *      observes both the initiation and any Reply-All confirmation.
   *
   * The previous auto-submit proxy route (POST /api/transfer-request) remains
   * available in the backend as dormant code — if AlumierMD ever change their
   * position and accept auto-submitted transfers, this function can be flipped
   * back to the old behavior with a one-line change.
   */
  function formatTransferDate(isoStr) {
    try {
      var d = new Date(isoStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) { return ''; }
  }

  function setTransferBtnState(btn, state, dateStr) {
    if (!btn) return;
    if (state === 'requested') {
      // HONEST state: the click only OPENED (or tried to open) the customer's
      // mail client \u2014 we do not know the email was sent until the inbound
      // webhook confirms our cc copy arrived. Saying "Requested" here made
      // webmail-only customers (whose mailto: silently no-ops) believe they
      // were done; they never sent, and their draft sat pending forever.
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M8 4v4l2.5 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg> Waiting for your email' + (dateStr ? ' \u2014 started ' + formatTransferDate(dateStr) : '') + '\u2026';
      btn.disabled = true;
      btn.style.opacity = '0.7';
      btn.style.cursor = 'default';
    } else if (state === 'received') {
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#7b9b72"/><path d="M4.5 8.5l2.5 2.5 4.5-4.5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> Transfer Request Received';
      btn.disabled = true;
      btn.style.opacity = '1';
      btn.style.cursor = 'default';
      btn.style.background = '#7b9b72';
    }
  }

  /**
   * Render the four webmail-compose alternatives into a target div.
   *
   * Background: a pure mailto: link works only if the customer has a
   * registered desktop mail handler (Apple Mail, Outlook desktop, Thunderbird).
   * Customers on Gmail / Outlook / Yahoo in a browser tab don't, so the
   * mailto: opens nothing visible and the email is never sent. This was the
   * dominant failure mode for clicked-not-sent customers on the AlumierMD
   * transfer flow.
   *
   * The fix is to give the customer four explicit "Open in <my webmail>"
   * options that target the webmail provider's compose deep-link. Each
   * link opens a new tab in their already-logged-in webmail with the email
   * pre-composed. They click Send. Email goes from THEIR address (not ours,
   * which AlumierMD rejects).
   *
   * Plus a fifth option: copy the email to clipboard so they can paste into
   * any other client (corporate webmail, ProtonMail, etc.).
   *
   * @param {HTMLElement} container - the div to render the buttons into
   * @param {string} to - primary recipient
   * @param {string} cc - CC list (literal commas, no %2C — see RFC notes
   *   in the cc=... build sites elsewhere in this file)
   * @param {string} subject - plain-text subject
   * @param {string} body - plain-text body
   */
  /**
   * Report which send path the customer used (telemetry). Fire-and-forget
   * with keepalive so the beacon survives the new tab the webmail links
   * open. Methods are allowlisted server-side in the proxy's
   * transfer-clicked route; unknown values are dropped there.
   */
  function alumierReportSendMethod(telemetry, method) {
    if (!telemetry || !telemetry.proxyUrl || !telemetry.customerId || !telemetry.email || !method) return;
    try {
      fetch(telemetry.proxyUrl + '/api/transfer-clicked', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          customerId: parseInt(telemetry.customerId, 10),
          email: telemetry.email,
          method: method
        })
      }).catch(function() {});
    } catch (e) {}
  }
  window.alumierReportSendMethod = alumierReportSendMethod;

  /**
   * Exposed on window so the inline-JS in snippets/alumier-account-status.liquid
   * (which runs in its own scope, not this DOMContentLoaded closure) can render
   * the same fallback panel for the account-page transfer flow.
   *
   * @param {Object} [telemetry] - optional {proxyUrl, customerId, email};
   *   when present, clicks on the provider buttons are reported as
   *   send-method telemetry.
   */
  function renderAlumierWebmailButtons(container, to, cc, subject, body, telemetry) {
    if (!container) return;

    // CC handling: each address must be percent-encoded individually then
    // joined with a literal comma. encodeURIComponent on the entire joined
    // string (e.g. "a@b.com,c@d.com") yields "%2C" between addresses, which
    // Outlook web in particular treats as part of an email address — it
    // silently drops the second address (and sometimes shows the TO twice).
    // Same root cause as the mailto: bugs we just fixed in PR #9.
    var ccAddresses = (cc || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    var ccLiteralCommas = ccAddresses.map(encodeURIComponent).join(',');

    // "Open in my email app" (mailto) — a first-class button, NOT an auto
    // navigation. App users (Apple Mail, Outlook desktop, Gmail/Mail app) get
    // one tap; webmail users ignore it and pick a provider below. On iOS we
    // surface it FIRST (Apple Mail is almost always the default handler);
    // everywhere else the provider buttons lead and this sits before Copy.
    var isIOS = /iP(hone|ad|od)/.test(navigator.userAgent || '');
    var mailtoHref = 'mailto:' + to +
      '?cc=' + (cc || '') +
      '&subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(body);
    var mailtoBtn =
      '<a href="' + mailtoHref + '" class="alumier-webmail-btn" data-provider="mailto">' +
        '<svg width="15" height="12" viewBox="0 0 24 18" xmlns="http://www.w3.org/2000/svg" style="margin-right:6px;flex-shrink:0;" fill="none" stroke="#475569" stroke-width="1.6">' +
          '<rect x="1" y="1" width="22" height="16" rx="1.5"/>' +
          '<path d="M2 3l10 7 10-7" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        'Open in my email app</a>';

    var qsBase =
      'to=' + encodeURIComponent(to) +
      '&cc=' + ccLiteralCommas +
      '&subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(body);
    var gmailUrl =
      'https://mail.google.com/mail/?view=cm&fs=1&' +
      'to=' + encodeURIComponent(to) +
      '&cc=' + ccLiteralCommas +
      '&su=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(body);
    // Outlook deeplink wants `path=/mail/action/compose` to land on the
    // compose form (without it the link opens the inbox shell). Both
    // outlook.live.com and outlook.office.com accept the same params.
    var outlookLiveUrl = 'https://outlook.live.com/mail/deeplink/compose?path=/mail/action/compose&' + qsBase;
    var outlookOfficeUrl = 'https://outlook.office.com/mail/deeplink/compose?path=/mail/action/compose&' + qsBase;
    var yahooUrl = 'https://compose.mail.yahoo.com/?' + qsBase;

    container.innerHTML =
      '<p style="margin:0 0 6px;font-size:14px;color:#78350f;">' +
        '<strong>Important: your transfer email has NOT been sent yet.</strong>' +
      '</p>' +
      '<p style="margin:0 0 10px;font-size:13px;color:#78350f;line-height:1.5;">' +
        'Choose how to send it below — we\'ll open a pre-filled email, you just press <em>Send</em>. ' +
        'Use your email service (Gmail, Outlook, Yahoo) if you read email in a browser, ' +
        'or <em>Open in my email app</em> if you use a mail app on this device. ' +
        'This panel turns green automatically when your email reaches us.' +
      '</p>' +
      // Prominent To/Cc card so customers using the Copy button know
      // exactly which address the email needs to be sent to. White
      // panel-on-amber gives clear contrast against the parent fallback
      // panel and the values themselves are larger/bolder than before.
      '<div style="margin:0 0 12px;padding:10px 12px;background:#fff;border:1px solid #f59e0b;border-radius:6px;font-size:14px;line-height:1.6;color:#1e293b;">' +
        '<div><span style="color:#92400e;font-weight:600;">Send to:</span> <strong style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">' + to + '</strong></div>' +
        '<div><span style="color:#92400e;font-weight:600;">Cc:</span> <span style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">' + (cc || '') + '</span></div>' +
      '</div>' +
      // Inline-SVG logos for each mail provider. Simple, recognisable shapes
      // with no external image deps. Logos are nominal-fair-use trademarks
      // — the same pattern every "Sign in with Google" button uses.
      '<div class="alumier-webmail-buttons" style="display:flex;flex-wrap:wrap;gap:8px;">' +
        (isIOS ? mailtoBtn : '') +
        '<a href="' + gmailUrl + '" target="_blank" rel="noopener" ' +
          'class="alumier-webmail-btn" data-provider="gmail">' +
          // Gmail: red envelope with white M shape (simplified)
          '<svg width="16" height="12" viewBox="0 0 24 18" xmlns="http://www.w3.org/2000/svg" style="margin-right:6px;flex-shrink:0;">' +
            '<rect width="24" height="18" rx="1.5" fill="#ea4335"/>' +
            '<path d="M2 3l10 7 10-7" stroke="#fff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>' +
          'Open in Gmail</a>' +
        '<a href="' + outlookLiveUrl + '" target="_blank" rel="noopener" ' +
          'class="alumier-webmail-btn" data-provider="outlook-live">' +
          // Outlook: blue square, white "O"
          '<svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="margin-right:6px;flex-shrink:0;">' +
            '<rect width="24" height="24" rx="2" fill="#0078d4"/>' +
            '<text x="12" y="18" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="16" font-weight="700" fill="#fff">O</text>' +
          '</svg>' +
          'Open in Outlook (personal)</a>' +
        '<a href="' + outlookOfficeUrl + '" target="_blank" rel="noopener" ' +
          'class="alumier-webmail-btn" data-provider="outlook-office">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="margin-right:6px;flex-shrink:0;">' +
            '<rect width="24" height="24" rx="2" fill="#0078d4"/>' +
            '<text x="12" y="18" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="16" font-weight="700" fill="#fff">O</text>' +
          '</svg>' +
          'Open in Outlook (work / school)</a>' +
        '<a href="' + yahooUrl + '" target="_blank" rel="noopener" ' +
          'class="alumier-webmail-btn" data-provider="yahoo">' +
          // Yahoo: purple "Y!"
          '<svg width="18" height="14" viewBox="0 0 30 24" xmlns="http://www.w3.org/2000/svg" style="margin-right:6px;flex-shrink:0;">' +
            '<text x="0" y="20" font-family="Arial,Helvetica,sans-serif" font-size="22" font-weight="700" fill="#6001d2">Y!</text>' +
          '</svg>' +
          'Open in Yahoo</a>' +
        (!isIOS ? mailtoBtn : '') +
        '<button type="button" class="alumier-webmail-btn" data-provider="copy" ' +
          'style="border:none;cursor:pointer;font:inherit;">' +
          // Copy: clipboard icon (slate stroke)
          '<svg width="14" height="14" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#475569" stroke-width="1.5" style="margin-right:6px;flex-shrink:0;">' +
            '<rect x="3" y="2.5" width="10" height="11" rx="1.5"/>' +
            '<path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" stroke-linecap="round"/>' +
          '</svg>' +
          'Copy email content</button>' +
      '</div>' +
      '<style>' +
        '.alumier-webmail-buttons .alumier-webmail-btn{' +
          'display:inline-flex;align-items:center;padding:8px 12px;background:#fff;' +
          'border:1px solid #e2e8f0;border-radius:6px;color:#1e293b;font-size:13px;' +
          'font-weight:500;text-decoration:none;line-height:1;' +
        '}' +
        '.alumier-webmail-buttons .alumier-webmail-btn:hover{' +
          'border-color:#94a3b8;background:#f8fafc;' +
        '}' +
        '.alumier-webmail-buttons .alumier-webmail-btn[data-copied]{' +
          'background:#dcfce7;border-color:#86efac;color:#166534;' +
        '}' +
      '</style>';

    // Send-method telemetry: report which provider button the customer
    // actually used (covers all anchors + the copy button via the shared
    // data-provider attribute). Fire-and-forget; never blocks the click.
    if (telemetry) {
      container.querySelectorAll('[data-provider]').forEach(function(el) {
        el.addEventListener('click', function() {
          alumierReportSendMethod(telemetry, el.getAttribute('data-provider'));
        });
      });
    }

    // Copy-to-clipboard wiring. Falls back to mailto: open if clipboard API
    // refuses (e.g. permission denied, non-secure context).
    var copyBtn = container.querySelector('[data-provider="copy"]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function() {
        var fullText =
          'To: ' + to + '\n' +
          'Cc: ' + cc + '\n' +
          'Subject: ' + subject + '\n\n' +
          body;
        var done = function() {
          copyBtn.setAttribute('data-copied', '1');
          var original = copyBtn.lastChild.textContent;
          // Repeat the destination address in the toast so the customer is
          // never in doubt about who to paste into their mail client's
          // To: field. Slightly longer label is acceptable here because
          // it's a transient confirmation, not the resting button label.
          copyBtn.lastChild.textContent = 'Copied — send to ' + to;
          setTimeout(function() {
            copyBtn.lastChild.textContent = original;
            copyBtn.removeAttribute('data-copied');
          }, 5000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(fullText).then(done).catch(function() {
            window.location.href = 'mailto:' + to +
              '?cc=' + cc +
              '&subject=' + encodeURIComponent(subject) +
              '&body=' + encodeURIComponent(body);
          });
        } else {
          window.location.href = 'mailto:' + to +
            '?cc=' + cc +
            '&subject=' + encodeURIComponent(subject) +
            '&body=' + encodeURIComponent(body);
        }
      });
    }
  }

  // Expose on window so the inline-JS in alumier-account-status.liquid
  // can reuse the same fallback UI.
  window.renderAlumierWebmailButtons = renderAlumierWebmailButtons;

  /**
   * After a transfer click, poll the proxy until the inbound webhook
   * confirms the customer's email actually arrived at our tracking cc
   * (emailConfirmed on the draft order). This is the only reliable
   * "the email was really sent" signal — browsers give zero feedback on
   * whether a mailto: opened anything.
   *
   * Polls every 30s for 10 minutes (20 attempts), then gives up quietly —
   * the on-page-load one-shot check covers later visits. Calls onConfirmed
   * exactly once. Shared by the PDP panel and the account page.
   */
  function alumierWatchEmailConfirmed(proxyUrl, customerId, onConfirmed) {
    if (!proxyUrl || !customerId || typeof onConfirmed !== 'function') return;
    var attempts = 0;
    var timer = setInterval(function() {
      attempts++;
      if (attempts > 20) { clearInterval(timer); return; }
      fetch(proxyUrl + '/api/draft-order/' + customerId, { method: 'GET' })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(d) {
          if (d && d.exists && d.draftOrder && d.draftOrder.emailConfirmed) {
            clearInterval(timer);
            onConfirmed();
          }
        })
        .catch(function() {});
    }, 30000);
    return timer;
  }
  window.alumierWatchEmailConfirmed = alumierWatchEmailConfirmed;

  /**
   * Resend-gate predicate. Given the ISO timestamp of the customer's
   * first transfer-request click, returns true only when ALL of:
   *   1. ≥ 24 hours have elapsed since the first send
   *   2. Now is within AlumierMD UK business hours
   *      (Mon–Fri, 10:00–15:59 local UK time)
   *
   * Outside business hours we keep the resend button hidden so the
   * customer doesn't fire off a request when AlumierMD CS isn't open
   * and queue up duplicates that arrive in a stale-looking inbox.
   * Customer's local clock is used as a UK-time proxy — Skintique is
   * UK-only, so the skew is zero in practice.
   *
   * Used by both the account-page snippet and the PDP panel handler.
   */
  function alumierShouldShowResend(sentAtISO) {
    if (!sentAtISO) return false;
    var sentAt = new Date(sentAtISO);
    if (isNaN(sentAt.getTime())) return false;
    var now = new Date();
    if ((now.getTime() - sentAt.getTime()) < 24 * 60 * 60 * 1000) return false;
    var d = now.getDay();   // 0 = Sun, 6 = Sat
    var h = now.getHours();
    if (d === 0 || d === 6) return false;        // weekends
    if (d === 5 && h >= 16) return false;        // Fri after 16:00
    if (h < 10) return false;                    // before 10:00 any weekday
    return true;
  }
  window.alumierShouldShowResend = alumierShouldShowResend;

  function autoRequestTransfer() {
    var transferKey = 'alumier_transfer_requested_' + (checkemail || 'anon');
    var transferDateKey = 'alumier_transfer_date_' + (checkemail || 'anon');
    var transferReceivedKey = 'alumier_transfer_received_' + (checkemail || 'anon');
    var requestDiv = document.getElementById('alumier-transfer-request');
    var confirmDiv = document.getElementById('alumier-transfer-confirm');
    var fallbackDiv = document.getElementById('alumier-transfer-fallback');
    var transferBtn = document.getElementById('alumier-transfer-btn');

    // Build the PDP transfer email body (used by initial click flow's
    // mailto AND the resend link, so we keep one source for both).
    function buildPdpTransferBody() {
      var firstName = customer_fname || '';
      var lastName = customer_lname || '';
      var fullName = (firstName + ' ' + lastName).trim() || checkemail;
      var subject = 'Account Transfer Request' + (fullName && fullName.indexOf('@') === -1 ? ' - ' + fullName : '');
      var body =
        'Dear AlumierMD Customer Services,\n\n' +
        'I would like to request that my AlumierMD account registration be transferred to Skintique.\n\n' +
        'Clinic: Skintique\n' +
        'Clinic reference: Y48Z4V9J\n\n' +
        'My details:\n' +
        '- Name: ' + fullName + '\n' +
        '- Email: ' + checkemail + '\n\n' +
        'I would be grateful if you could process this at your earliest convenience.\n\n' +
        'Kind regards,\n' +
        fullName;
      return { subject: subject, body: body };
    }

    // Re-fire the transfer email without creating a new draft order.
    // Used by the resend link — the customer's draft order already exists
    // from the original send, so we just need to re-trigger the customer's
    // mail client + webmail-fallback panel and bump the timestamp so the
    // next 24h gate starts fresh.
    function pdpResendTransferEmail() {
      var em = buildPdpTransferBody();
      var nowIso = new Date().toISOString();
      // No auto mailto: navigation — it silently fails for browser-webmail
      // users (nothing opens). The panel below leads with provider buttons
      // plus an explicit "Open in my email app" option, so every device has a
      // working path.
      try {
        localStorage.setItem(transferDateKey, nowIso);
      } catch (ex) {}
      setTransferBtnState(transferBtn, 'requested', nowIso);
      if (fallbackDiv) {
        renderAlumierWebmailButtons(
          fallbackDiv,
          'customerservice@alumierlabs.com',
          'transfers@skintique.co.uk,transfers@mail.skintique.co.uk',
          em.subject,
          em.body,
          { proxyUrl: PROXY_URL, customerId: shopifyCustomerId, email: checkemail }
        );
        fallbackDiv.style.display = 'block';
      }
      // Resending starts a fresh 24h gate.
      var resendWrap = document.getElementById('alumier-transfer-resend-wrap');
      if (resendWrap) resendWrap.style.display = 'none';
      // Track click for Command Center pipeline (each send = one click).
      if (shopifyCustomerId && PROXY_URL) {
        fetch(PROXY_URL + '/api/transfer-clicked', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: parseInt(shopifyCustomerId, 10), email: checkemail, method: 'resend' })
        }).catch(function() {});
      }
      // Watch for real arrival of the resent email and flip to received.
      alumierWatchEmailConfirmed(PROXY_URL, shopifyCustomerId, function() {
        try { localStorage.setItem(transferReceivedKey, '1'); } catch (ex) {}
        setTransferBtnState(transferBtn, 'received');
        if (fallbackDiv) fallbackDiv.style.display = 'none';
        var statusEl = document.getElementById('alumier-transfer-confirm-status');
        if (statusEl) statusEl.style.display = 'block';
      });
    }

    function maybePdpShowResend() {
      var sentAt = localStorage.getItem(transferDateKey);
      var canResend = typeof window.alumierShouldShowResend === 'function'
        ? window.alumierShouldShowResend(sentAt)
        : false;
      var resendWrap = document.getElementById('alumier-transfer-resend-wrap');
      if (resendWrap) resendWrap.style.display = canResend ? 'block' : 'none';
    }

    var pdpResendLink = document.getElementById('alumier-transfer-resend');
    if (pdpResendLink && !pdpResendLink._wired) {
      pdpResendLink._wired = true;
      pdpResendLink.addEventListener('click', function(e) {
        e.preventDefault();
        pdpResendTransferEmail();
      });
    }

    // Already requested — restore button state
    try {
      var wasReceived = localStorage.getItem(transferReceivedKey);
      var wasRequested = localStorage.getItem(transferKey);
      var requestDate = localStorage.getItem(transferDateKey);
      if (wasReceived) {
        setTransferBtnState(transferBtn, 'received');
        if (confirmDiv) confirmDiv.style.display = 'block';
        // Inbound webhook confirmed the email landed at our tracking
        // address — the customer doesn't need the webmail-buttons fallback
        // any more. Hide it to clean up the UI.
        if (fallbackDiv) fallbackDiv.style.display = 'none';
        return;
      }
      if (wasRequested) {
        setTransferBtnState(transferBtn, 'requested', requestDate);
        if (confirmDiv) confirmDiv.style.display = 'block';
        maybePdpShowResend();
        // Check if email was confirmed since last visit
        if (shopifyCustomerId && PROXY_URL) {
          fetchWithTimeout(PROXY_URL + '/api/draft-order/' + shopifyCustomerId, { method: 'GET' }, API_TIMEOUT_MS)
          .then(function(r) { return r.json(); })
          .then(function(d) {
            if (d.exists && d.draftOrder && d.draftOrder.emailConfirmed) {
              try { localStorage.setItem(transferReceivedKey, '1'); } catch (ex) {}
              setTransferBtnState(transferBtn, 'received');
              // Hide the webmail-buttons fallback — confirmation arrived.
              if (fallbackDiv) fallbackDiv.style.display = 'none';
              var resendWrapHide = document.getElementById('alumier-transfer-resend-wrap');
              if (resendWrapHide) resendWrapHide.style.display = 'none';
            }
          }).catch(function() {});
        }
        return;
      }
    } catch (e) {}

    if (!checkemail) {
      console.warn('AlumierMD: cannot request transfer without customer email');
      if (transferBtn) {
        transferBtn.textContent = 'Request Transfer to Skintique';
        transferBtn.disabled = false;
      }
      return;
    }

    // Read the current PDP variant ID (same DOM lookup as saveForLater below)
    var variantInput = document.querySelector('.product-info__add-to-cart input[name="id"], form[action*="/cart/add"] input[name="id"]');
    var variantId = variantInput ? parseInt(variantInput.value, 10) : 0;

    if (!shopifyCustomerId || !variantId) {
      console.warn('AlumierMD: missing customerId or variantId, cannot create draft order', {
        shopifyCustomerId: shopifyCustomerId,
        variantId: variantId
      });
      if (transferBtn) {
        transferBtn.textContent = 'Request Transfer to Skintique';
        transferBtn.disabled = false;
      }
      return;
    }

    // Step 1: create (or append to) the pending draft order via the proxy.
    fetchWithTimeout(PROXY_URL + '/api/draft-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: parseInt(shopifyCustomerId, 10),
        email: checkemail,
        variantId: variantId,
        quantity: 1
      })
    }, API_TIMEOUT_MS)
    .then(function(response) { return response.json(); })
    .then(function(data) {
      if (!data || !data.success || !data.draftOrderId) {
        throw new Error((data && data.error) || 'Draft order creation failed');
      }

      var draftOrderId = data.draftOrderId;
      console.log('AlumierMD: draft order ready for transfer request', { draftOrderId: draftOrderId });

      // Mark as requested so a second click doesn't re-open the mail client
      var now = new Date().toISOString();
      try {
        localStorage.setItem(transferKey, '1');
        localStorage.setItem(transferDateKey, now);
      } catch (e) {}

      // Fire Klaviyo event BEFORE the mailto redirect — browser navigation to
      // mail clients can interrupt pending network calls.
      trackKlaviyoEvent('AlumierMD Transfer Email Initiated', {
        email: checkemail,
        draft_order_id: draftOrderId,
        product_url: window.location.href,
        source: 'pdp'
      });

      // Update button to "Transfer Requested — date" (stays visible, disabled)
      setTransferBtnState(transferBtn, 'requested', now);
      if (confirmDiv) confirmDiv.style.display = 'block';

      // Fire transfer-clicked to proxy for Command Center tracking
      if (shopifyCustomerId && PROXY_URL) {
        fetch(PROXY_URL + '/api/transfer-clicked', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: parseInt(shopifyCustomerId, 10), email: checkemail, method: 'mailto' })
        }).catch(function() {});
      }

      // Step 2: build and open the mailto with the draft order ID as the ref.
      var firstName = customer_fname || '';
      var lastName = customer_lname || '';
      var fullName = (firstName + ' ' + lastName).trim() || checkemail;

      // Clean subject — no reference numbers or order IDs. AlumierMD CS
      // only need the request; our cron poll handles completion detection
      // via direct AlumierMD API polling (no subject-line correlation needed).
      var subject = 'Account Transfer Request' + (fullName && fullName.indexOf('@') === -1 ? ' - ' + fullName : '');
      var body =
        'Dear AlumierMD Customer Services,\n\n' +
        'I would like to request that my AlumierMD account registration be transferred to Skintique.\n\n' +
        'Clinic: Skintique\n' +
        'Clinic reference: Y48Z4V9J\n\n' +
        'My details:\n' +
        '- Name: ' + fullName + '\n' +
        '- Email: ' + checkemail + '\n\n' +
        'I would be grateful if you could process this at your earliest convenience.\n\n' +
        'Kind regards,\n' +
        fullName;

      // CC must NOT be percent-encoded as a single string — encodeURIComponent
      // would turn the comma separator into %2C, which Gmail web / Yahoo web
      // / Outlook web silently drop, killing the tracking address. Literal
      // comma in the URL works across every mail client we've tested.
      // No auto mailto: navigation. On browser-webmail users (Gmail / Outlook
      // / Yahoo web) a mailto: does nothing visible, so leading with it just
      // produced a dead tap. Instead we render the compose panel immediately —
      // it leads with the provider buttons and offers an explicit "Open in my
      // email app" (mailto) option for people who do have a mail app.

      // Render the webmail compose buttons. The fallbackDiv is the
      // amber panel below the Request Transfer button (defined in
      // snippets/alumier-block.liquid). Customers whose system mailto: opens
      // can ignore the panel; everyone else can pick their webmail provider.
      if (fallbackDiv) {
        renderAlumierWebmailButtons(
          fallbackDiv,
          'customerservice@alumierlabs.com',
          'transfers@skintique.co.uk,transfers@mail.skintique.co.uk',
          subject,
          body,
          { proxyUrl: PROXY_URL, customerId: shopifyCustomerId, email: checkemail }
        );
        fallbackDiv.style.display = 'block';
      }

      // Watch for the inbound webhook to confirm the email actually arrived,
      // then flip the whole panel to the green received state in real time.
      alumierWatchEmailConfirmed(PROXY_URL, shopifyCustomerId, function() {
        try { localStorage.setItem(transferReceivedKey, '1'); } catch (ex) {}
        setTransferBtnState(transferBtn, 'received');
        if (fallbackDiv) fallbackDiv.style.display = 'none';
        var statusEl = document.getElementById('alumier-transfer-confirm-status');
        if (statusEl) statusEl.style.display = 'block';
      });
    })
    .catch(function(err) {
      console.warn('AlumierMD: transfer request failed:', err.message);
      // Reset button so they can retry. Do NOT open mailto on failure —
      // a mailto without a draft order ref is useless for webhook correlation.
      if (transferBtn) {
        transferBtn.textContent = 'Request Transfer to Skintique';
        transferBtn.disabled = false;
      }
    });
  }

  /* ----------------------------- RecurPay gate bridge -----------------------------
     Thin wrappers over the global gate defined at the top of this file. Keeping them here means
     showAlumierState() has exactly one way to talk about RecurPay visibility. */
  function setRecurpayAllowed(allowed) {
    if (window.__alumierRecurpay) window.__alumierRecurpay.setAllowed(allowed);
  }
  function enforceRecurpayVisibility() {
    if (window.__alumierRecurpay) window.__alumierRecurpay.enforce();
  }

  /* ----------------------------- Show verification state on PDP ----------------------------- */
  function showAlumierState(state) {
    if (!ALUMIER_ATC_GATE_ENABLED) return; // new 'checkout' flow: gate off - never hide/show add to cart or tag the cart
    // RecurPay "Subscribe & Save" mirrors the add-to-cart button exactly: visible only when the
    // customer is cleared to buy. Set the single source of truth BEFORE anything else so the
    // alumier:state listeners (and the MutationObserver) already see the correct desired value.
    setRecurpayAllowed(state === 'patient_owned' || state === 'fail_open');

    // Publish state so other sections (e.g. Complete the Routine) can react.
    try {
      window.__alumierPdpState = state;
      document.dispatchEvent(new CustomEvent('alumier:state', { detail: { state: state } }));
    } catch (e) { /* non-fatal */ }
    var wrapper = document.querySelector('.alumier-block--wrapper');
    var stateA = document.getElementById('alumier-state-verified');
    var stateB = document.getElementById('alumier-state-transfer');
    var stateC = document.getElementById('alumier-state-not-found');
    var stateLoggedOut = document.getElementById('alumier-state-logged-out');
    var stateLoading = document.getElementById('alumier-state-loading');
    var addToCart = document.querySelector('.product-alumiermd .product-info__add-to-cart');
    var shopifyBtns = document.querySelector('.product-alumiermd .shopify-payment-button');

    // Hide all state blocks
    [stateA, stateB, stateC, stateLoggedOut, stateLoading].forEach(function(el) {
      if (el) el.style.display = 'none';
    });

    if (!wrapper) return;

    // Add initialized class so CSS !important unlock kicks in (anti-FOUC)
    wrapper.classList.add('alumier-initialized');

    if (state === 'loading') {
      wrapper.style.display = 'block';
      if (stateLoading) stateLoading.style.display = 'block';
      if (addToCart) addToCart.style.display = 'none';
      if (shopifyBtns) shopifyBtns.style.display = 'none';
      enforceRecurpayVisibility();
      return;
    }

    if (state === 'patient_owned') {
      wrapper.style.display = 'block';
      if (stateA) stateA.style.display = 'block';
      if (addToCart) addToCart.style.display = 'flex';
      if (shopifyBtns) shopifyBtns.style.display = 'block';
      enforceRecurpayVisibility();
      setCachedResult('patient_owned', null);
      return;
    }

    if (state === 'patient_not_owned') {
      wrapper.style.display = 'block';
      if (stateB) stateB.style.display = 'block';
      if (addToCart) addToCart.style.display = 'none';
      if (shopifyBtns) shopifyBtns.style.display = 'none';
      enforceRecurpayVisibility();
      trackKlaviyoEvent('AlumierMD Transfer Wall Shown', {
        email: checkemail,
        product_url: window.location.href
      });

      // If already requested, restore button state
      var transferKey = 'alumier_transfer_requested_' + (checkemail || 'anon');
      var transferDateKey = 'alumier_transfer_date_' + (checkemail || 'anon');
      var transferReceivedKey = 'alumier_transfer_received_' + (checkemail || 'anon');
      try {
        var transferBtn = document.getElementById('alumier-transfer-btn');
        var confirmDiv = document.getElementById('alumier-transfer-confirm');
        var wasReceived = localStorage.getItem(transferReceivedKey);
        var wasRequested = localStorage.getItem(transferKey);
        var requestDate = localStorage.getItem(transferDateKey);
        if (wasReceived) {
          setTransferBtnState(transferBtn, 'received');
          if (confirmDiv) confirmDiv.style.display = 'block';
        } else if (wasRequested) {
          setTransferBtnState(transferBtn, 'requested', requestDate);
          if (confirmDiv) confirmDiv.style.display = 'block';
        }
      } catch (e) {}

      // Wire up the transfer button (customer must click to confirm)
      var transferBtn = document.getElementById('alumier-transfer-btn');
      if (transferBtn && !transferBtn.disabled) {
        transferBtn.addEventListener('click', function() {
          transferBtn.textContent = 'Requesting...';
          transferBtn.disabled = true;
          autoRequestTransfer();
        });
      }

      // Wire up the info toggle
      var infoToggle = document.getElementById('alumier-info-toggle');
      var infoPanel = document.getElementById('alumier-info-panel');
      if (infoToggle && infoPanel) {
        infoToggle.addEventListener('click', function(e) {
          e.preventDefault();
          var isOpen = infoPanel.style.display !== 'none';
          infoPanel.style.display = isOpen ? 'none' : 'block';
          infoToggle.setAttribute('aria-expanded', !isOpen);
        });
      }
      return;
    }

    if (state === 'patient_not_found') {
      wrapper.style.display = 'block';
      if (stateC) stateC.style.display = 'block';
      if (addToCart) addToCart.style.display = 'none';
      if (shopifyBtns) shopifyBtns.style.display = 'none';
      enforceRecurpayVisibility();

      // Wire up PDP registration form (consent-gated)
      var regConsent = document.getElementById('alumier-reg-consent');
      var regSubmitBtn = document.getElementById('alumier-reg-submit');
      if (regConsent && regSubmitBtn) {
        regConsent.addEventListener('change', function() {
          regSubmitBtn.disabled = !regConsent.checked;
          regSubmitBtn.style.opacity = regConsent.checked ? '1' : '0.5';
          regSubmitBtn.style.cursor = regConsent.checked ? 'pointer' : 'not-allowed';
        });
      }
      var regForm = document.getElementById('alumier-pdp-register-form');
      if (regForm) {
        regForm.addEventListener('submit', function(e) {
          e.preventDefault();
          if (!regConsent || !regConsent.checked) return;
          var fname = document.getElementById('alumier-reg-fname').value.trim();
          var lname = document.getElementById('alumier-reg-lname').value.trim();
          var regEmail = document.getElementById('alumier-reg-email').value.trim();
          if (!fname || !regEmail) return;

          regSubmitBtn.disabled = true;
          regSubmitBtn.textContent = 'Registering...';
          var progressEl = document.getElementById('alumier-reg-progress');
          var errorEl = document.getElementById('alumier-reg-error');
          if (progressEl) progressEl.style.display = 'block';
          if (errorEl) errorEl.style.display = 'none';

          fetchWithTimeout(HEROKU_URL + '/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: regEmail, first_name: fname, last_name: lname })
          }, API_TIMEOUT_MS)
          .then(function(r) { return r.json(); })
          .then(function(result) {
            if (result.error) throw new Error(result.error);
            // Re-verify
            return fetchWithTimeout(CHECK_EMAIL_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: regEmail })
            }, API_TIMEOUT_MS);
          })
          .then(function(r) { if (r && r.json) return r.json(); })
          .then(function(verifyData) {
            if (progressEl) progressEl.style.display = 'none';
            if (verifyData && verifyData.verification_response) {
              var code = verifyData.verification_response.reason_code;
              if (code === 'patient_owned') {
                setCachedResult(verifyData.verification_response);
                showAlumierState('patient_owned');
              } else if (code === 'patient_not_owned') {
                showAlumierState('patient_not_owned');
              } else {
                showAlumierState('patient_owned');
              }
            } else {
              showAlumierState('patient_owned');
            }
          })
          .catch(function(err) {
            console.error('AlumierMD PDP registration failed:', err);
            if (progressEl) progressEl.style.display = 'none';
            if (errorEl) errorEl.style.display = 'block';
            regSubmitBtn.disabled = false;
            regSubmitBtn.textContent = 'Register with AlumierMD';
            regSubmitBtn.style.opacity = '1';
          });
        });
      }
      return;
    }

    if (state === 'logged_out') {
      wrapper.style.display = 'block';
      if (stateLoggedOut) stateLoggedOut.style.display = 'block';
      if (addToCart) addToCart.style.display = 'none';
      if (shopifyBtns) shopifyBtns.style.display = 'none';
      enforceRecurpayVisibility();
      return;
    }

    if (state === 'fail_open') {
      // API failed — allow purchase but flag the cart for human review
      wrapper.style.display = 'none';
      if (addToCart) addToCart.style.display = 'flex';
      if (shopifyBtns) shopifyBtns.style.display = 'block';
      enforceRecurpayVisibility();

      // Tag the cart so Shopify Flow can pause the order for verification
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'alumier-verification': 'api-failed',
            'alumier-fail-time': new Date().toISOString(),
            'alumier-customer-email': checkemail || 'unknown'
          }
        })
      }).catch(function() {});
      return;
    }
  }

  /* ----------------------------- Main API check (PDP) ----------------------------- */
  function checkAlumierEmail() {
    if (!ALUMIER_ATC_GATE_ENABLED) return; // new 'checkout' flow: gate off - no verification call on page load
    if (!checkemail) {
      // Not logged in
      if (document.body.classList.contains('product-alumiermd')) {
        showAlumierState('logged_out');
      }
      return;
    }

    // Check cache first — only use for patient_owned
    var cached = getCachedResult();
    if (cached && cached.reason_code === 'patient_owned') {
      showAlumierState('patient_owned');
      return;
    }

    // Show loading skeleton
    if (document.body.classList.contains('product-alumiermd')) {
      showAlumierState('loading');
    }

    fetchWithTimeout(CHECK_EMAIL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: checkemail })
    }, API_TIMEOUT_MS)
    .then(function(response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    })
    .then(function(data) {
      console.log('AlumierMD verification:', data);
      if (data.verification_response && data.verification_response.reason_code) {
        var code = data.verification_response.reason_code;
        var msg = data.verification_response.message || '';

        if (code === 'patient_owned' && msg.includes('belongs to you')) {
          showAlumierState('patient_owned');
        } else if (code === 'patient_not_owned' && msg.includes('registered with another provider')) {
          showAlumierState('patient_not_owned');
        } else {
          // patient_not_found — show registration form (consent required)
          showAlumierState('patient_not_found');
        }
      } else {
        // Unknown response — show registration form
        showAlumierState('patient_not_found');
      }
    })
    .catch(function(error) {
      console.error('AlumierMD API check failed:', error);
      alertAdminOnFailure(error.message || 'Unknown error');
      document.dispatchEvent(new CustomEvent('alumier:api:failed', { detail: { error: error } }));
      // Fail open — allow purchase when API is down
      showAlumierState('fail_open');
    });
  }

  /* ----------------------------- Collection/recommendation card buttons ----------------------------- */
  function hideButtons() {
    // New 'checkout' flow: gate off. Cards render their normal add-to-basket / Choose Options buttons and this must
    // not touch them. (This also stops the old side effect where the shared .hide-detail-button class
    // hid "Choose Options" on non-AlumierMD Wizzy cards.) window.alumierHideButtons stays exported as a
    // harmless no-op for assets/cart-drawer.js, assets/product-recommendations.js, sections/recently-viewed.liquid.
    if (!ALUMIER_ATC_GATE_ENABLED) return;
    if (!checkemail) {
      // Not logged in — show login button on collection cards
      applyCardButtonState('logged_out');
      return;
    }

    var cached = getCachedResult();
    if (cached) {
      applyCardButtonState(cached.reason_code);
      return;
    }

    fetchWithTimeout(CHECK_EMAIL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: checkemail })
    }, API_TIMEOUT_MS)
    .then(function(response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    })
    .then(function(data) {
      if (data.verification_response && data.verification_response.reason_code) {
        applyCardButtonState(data.verification_response.reason_code);
      }
    })
    .catch(function(error) {
      console.error('AlumierMD hideButtons check failed:', error);
      // Fail open on cards — show add-to-cart
      applyCardButtonState('patient_owned');
    });
  }

  function applyCardButtonState(reasonCode) {
    var selectors = ['product-card', '.card'];
    selectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(card) {
        var viewBtn = card.querySelector('.hide-detail-button');
        var atcBtn = card.querySelector('.hide-atc-button');
        var loginBtn = card.querySelector('.alumier-login-btn');
        var verifyBtn = card.querySelector('.alumier-verify-btn');

        // Hide all first
        if (viewBtn) viewBtn.style.display = 'none';
        if (atcBtn) atcBtn.style.display = 'none';
        if (loginBtn) loginBtn.style.display = 'none';
        if (verifyBtn) verifyBtn.style.display = 'none';

        if (reasonCode === 'patient_owned') {
          // Verified — show Add to Cart
          if (atcBtn) atcBtn.style.display = 'block';
        } else if (reasonCode === 'logged_out') {
          // Not logged in — show Log in button
          if (loginBtn) loginBtn.style.display = 'block';
        } else {
          // patient_not_owned or patient_not_found — show Verification needed
          if (verifyBtn) verifyBtn.style.display = 'block';
        }
      });
    });
  }

  /* ----------------------------- Transfer email mailto helper ----------------------------- */
  function openTransferEmail() {
    var subject = encodeURIComponent('Transfer Clinic Registration');
    var body = encodeURIComponent(
      'Dear AlumierMD Customer Services Team,\n' +
      'Can you please transfer my registration details to Skintique (Clinic reference Y48Z4V9J)\n' +
      'Kind regards'
    );
    // CC: literal comma between addresses (not %2C) so webmail clients honour
    // both. transfers@skintique.co.uk is the dedicated human-monitored
    // inbox (M365); transfers@mail.skintique.co.uk is the Resend-inbound MX
    // that auto-fires completeTransfer() when AlumierMD replies-all.
    window.location.href = 'mailto:customerservice@alumierlabs.com?cc=transfers@skintique.co.uk,transfers@mail.skintique.co.uk&subject=' + subject + '&body=' + body;

    trackKlaviyoEvent('AlumierMD Transfer Email Clicked', {
      email: checkemail,
      product_url: window.location.href
    });
  }

  /* ----------------------------- "Add to My Order" button (Phase 7v2) ----------------------------- */
  function showSaveConfirm(productTitle, itemCount) {
    var confirmEl = document.getElementById('alumier-save-confirm');
    if (confirmEl) {
      var titleEl = document.getElementById('alumier-save-confirm-title');
      var textEl = document.getElementById('alumier-save-confirm-text');
      if (titleEl) titleEl.textContent = (productTitle || 'Product') + ' added to your AlumierMD draft order!';
      if (textEl) textEl.textContent = 'You have ' + (itemCount || 1) + ' item' + ((itemCount || 1) > 1 ? 's' : '') + ' saved. Browse more AlumierMD products and add them to your draft order before completing checkout.';
      confirmEl.style.display = 'block';
    }
    updateSaveCount(itemCount || 1);
  }

  var saveForLaterBtn = document.getElementById('alumier-save-for-later');
  if (saveForLaterBtn) {
    saveForLaterBtn.addEventListener('click', function(e) {
      e.preventDefault();

      // Get the current variant ID from the product form
      var variantInput = document.querySelector('.product-info__add-to-cart input[name="id"], form[action*="/cart/add"] input[name="id"]');
      var variantId = variantInput ? variantInput.value : '';

      // Gather product data for localStorage fallback
      var productData = {
        title: document.querySelector('.product-info__title, h1.product__title, [data-product-title]') ?
          document.querySelector('.product-info__title, h1.product__title, [data-product-title]').textContent.trim() : document.title,
        url: window.location.pathname,
        image: (document.querySelector('.product-info__media img, .product__media img, [data-product-featured-image]') || {}).src || '',
        price: document.querySelector('.product-info__price .price, .price--regular .money, [data-product-price]') ?
          document.querySelector('.product-info__price .price, .price--regular .money, [data-product-price]').textContent.trim() : '',
        variantId: variantId,
        savedAt: new Date().toISOString()
      };

      // Show loading state on button
      var originalText = saveForLaterBtn.textContent;
      saveForLaterBtn.textContent = 'Adding...';
      saveForLaterBtn.disabled = true;

      // Try Railway proxy first, fall back to localStorage
      if (shopifyCustomerId && variantId && PROXY_URL) {
        fetchWithTimeout(PROXY_URL + '/api/draft-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: parseInt(shopifyCustomerId, 10),
            email: checkemail,
            variantId: parseInt(variantId, 10),
            quantity: 1
          })
        }, API_TIMEOUT_MS)
        .then(function(response) { return response.json(); })
        .then(function(data) {
          if (data.success) {
            console.log('AlumierMD: added to draft order', data);
            saveForLaterBtn.textContent = 'Added!';
            saveForLaterBtn.classList.add('alumier-btn--disabled');
            showSaveConfirm(productData.title, data.itemCount);

            // Also save to localStorage as backup
            saveToLocalStorage(productData);
          } else {
            throw new Error(data.error || 'Unknown error');
          }
        })
        .catch(function(err) {
          console.warn('AlumierMD proxy unavailable, saving to localStorage:', err.message);
          saveToLocalStorage(productData);
          saveForLaterBtn.textContent = 'Added!';
          saveForLaterBtn.classList.add('alumier-btn--disabled');
          showSaveConfirm(productData.title, null);
        });
      } else {
        // No proxy available or missing data — use localStorage directly
        saveToLocalStorage(productData);
        saveForLaterBtn.textContent = 'Added!';
        saveForLaterBtn.classList.add('alumier-btn--disabled');
        showSaveConfirm(productData.title, null);
      }

      // Fire Klaviyo event
      trackKlaviyoEvent('AlumierMD Item Saved', {
        email: checkemail,
        product_title: productData.title,
        product_url: productData.url,
        variant_id: variantId
      });
    });

    // On page load: check if already in pending order (proxy or localStorage)
    if (shopifyCustomerId && PROXY_URL) {
      fetchWithTimeout(PROXY_URL + '/api/draft-order/' + shopifyCustomerId, {
        method: 'GET'
      }, API_TIMEOUT_MS)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.exists && data.draftOrder) {
          updateSaveCount(data.draftOrder.lineItems.length);
          // Check if current variant is already in the order
          var variantInput = document.querySelector('.product-info__add-to-cart input[name="id"], form[action*="/cart/add"] input[name="id"]');
          var currentVariant = variantInput ? parseInt(variantInput.value, 10) : 0;
          var alreadyInOrder = data.draftOrder.lineItems.some(function(item) { return item.variantId === currentVariant; });
          if (alreadyInOrder) {
            saveForLaterBtn.textContent = 'Already in your order';
            saveForLaterBtn.disabled = true;
            saveForLaterBtn.classList.add('alumier-btn--disabled');
          }
        }
      })
      .catch(function() {
        // Proxy unavailable — check localStorage instead
        checkLocalStorageSaved();
      });
    } else {
      checkLocalStorageSaved();
    }
  }

  function checkLocalStorageSaved() {
    try {
      var existingSaved = localStorage.getItem('alumier_saved_items_' + (checkemail || 'anon'));
      if (existingSaved) {
        var existingParsed = JSON.parse(existingSaved);
        if (existingParsed.items && existingParsed.items.length > 0) {
          updateSaveCount(existingParsed.items.length);
          var currentUrl = window.location.pathname;
          var alreadySaved = existingParsed.items.some(function(item) { return item.url === currentUrl; });
          if (alreadySaved && saveForLaterBtn) {
            saveForLaterBtn.textContent = 'Already in your order';
            saveForLaterBtn.disabled = true;
            saveForLaterBtn.classList.add('alumier-btn--disabled');
          }
        }
      }
    } catch (e) {}
  }

  function saveToLocalStorage(productData) {
    try {
      var savedRaw = localStorage.getItem('alumier_saved_items_' + (checkemail || 'anon'));
      var saved = savedRaw ? JSON.parse(savedRaw) : { items: [] };
      var exists = saved.items.some(function(item) { return item.url === productData.url; });
      if (!exists) {
        saved.items.push(productData);
        localStorage.setItem('alumier_saved_items_' + (checkemail || 'anon'), JSON.stringify(saved));
      }
      updateSaveCount(saved.items.length);
    } catch (e) {
      console.error('AlumierMD localStorage save failed:', e);
    }
  }

  function updateSaveCount(count) {
    var countEl = document.getElementById('alumier-save-count');
    if (countEl && count > 0) {
      countEl.style.display = 'block';
      var countTextEl = document.getElementById('alumier-save-count-text');
      if (countTextEl) {
        countTextEl.textContent = 'Your saved order: ' + count + ' item' + (count > 1 ? 's' : '');
      } else {
        countEl.textContent = count + ' product' + (count > 1 ? 's' : '') + ' in your pending order';
      }
    }
  }

  /* ----------------------------- "Recheck" button for transfer wall ----------------------------- */
  var recheckBtn = document.getElementById('alumier-recheck-btn');
  if (recheckBtn) {
    recheckBtn.addEventListener('click', function(e) {
      e.preventDefault();
      sessionStorage.removeItem(CACHE_KEY);
      checkAlumierEmail();
    });
  }

  /* ----------------------------- "Try Again" button for registration failure ----------------------------- */
  var retryBtn = document.getElementById('alumier-retry-btn');
  if (retryBtn) {
    retryBtn.addEventListener('click', function(e) {
      e.preventDefault();
      sessionStorage.removeItem(CACHE_KEY);
      showAlumierState('loading');
      checkAlumierEmail();
    });
  }

  /* ----------------------------- Transfer email CTA button ----------------------------- */
  var transferEmailBtn = document.getElementById('alumier-transfer-email-btn');
  if (transferEmailBtn) {
    transferEmailBtn.addEventListener('click', function(e) {
      e.preventDefault();
      openTransferEmail();
      // Show confirmation message
      var confirmMsg = document.getElementById('alumier-transfer-confirm');
      if (confirmMsg) confirmMsg.style.display = 'block';
    });
  }

  /* ----------------------------- Register button (new customer) ----------------------------- */
  var registerBtnAlumier = document.getElementById('register-btn-alumier');
  var popup = document.getElementById('registerPopup');
  var proceedBtn = document.getElementById('proceed-btn');
  var cancelBtnPopup = document.getElementById('cancel-btn-popup');

  if (registerBtnAlumier && popup) {
    registerBtnAlumier.addEventListener('click', function() {
      popup.style.display = 'flex';
    });
  }

  if (proceedBtn) {
    proceedBtn.addEventListener('click', function(e) {
      e.preventDefault();
      var email = document.getElementById('alumier-customer-id').value;
      if (!email) return;

      // Subscribe to Klaviyo list
      fetch('https://a.klaviyo.com/client/subscriptions', {
        method: 'POST',
        headers: {
          'accept': 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          'revision': '2025-01-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    properties: { email: email, first_name: customer_fname, last_name: customer_lname },
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: 'Smvg4S' } } }
          }
        })
      }).catch(function(err) { console.error('Klaviyo subscribe failed:', err); });

      // Check email via Heroku
      fetchWithTimeout(CHECK_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      }, API_TIMEOUT_MS)
      .then(function(r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function(data) {
        if (data.verification_response && data.verification_response.message &&
            data.verification_response.message.includes('registered with another provider')) {
          // Show transfer state inline (close popup, show state B)
          if (popup) popup.style.display = 'none';
          showAlumierState('patient_not_owned');
        } else {
          // Registration success
          registerAlumier();
          if (popup) popup.style.display = 'none';
          showAlumierState('patient_owned');
        }
      })
      .catch(function(err) {
        console.error('AlumierMD proceedBtn check failed:', err);
        alertAdminOnFailure(err.message || 'Unknown error');
        if (popup) popup.style.display = 'none';
        showAlumierState('fail_open');
      });
    });
  }

  if (cancelBtnPopup) {
    cancelBtnPopup.addEventListener('click', function(e) {
      e.preventDefault();
      if (popup) popup.style.display = 'none';
    });
  }

  /* ----------------------------- "Create Account" button on logged-out state ----------------------------- */
  var registerBtn = document.getElementById('register-btn');
  if (registerBtn) {
    registerBtn.addEventListener('click', function() {
      setTimeout(function() {
        document.cookie = 'savedRegisterUrl=' + window.location.href + '; path=/; max-age=3600; SameSite=Lax; Secure';
        window.location.href = '/account/register';
      }, 300);
    });
  }

  /* ----------------------------- Login button (saves URL, redirects to /account) ----------------------------- */
  var loginLink = document.getElementById('login-link');
  if (loginLink) {
    loginLink.addEventListener('click', function(event) {
      event.preventDefault();
      if (savedRegisterUrl) {
        document.cookie = 'savedRegisterUrl=; path=/; max-age=0; SameSite=Lax; Secure';
      }
      setTimeout(function() { window.location.href = '/account'; }, 500);
    });
  }

  var alumierLoginBtn = document.getElementById('alumier-login-button');
  if (alumierLoginBtn) {
    alumierLoginBtn.addEventListener('click', function(e) {
      e.preventDefault();
      localStorage.setItem('savedUrlLogin', window.location.href);
      setTimeout(function() { window.location.href = '/account'; }, 500);
    });
  }

  /* ----------------------------- Cookie-based registration redirect ----------------------------- */
  var savedRegisterUrl = getCookie('savedRegisterUrl');

  if (savedRegisterUrl && document.body.classList.contains('template--register')) {
    var applyAlumier = document.getElementById('apply-alumier');
    var applyAlumierWrap = document.getElementById('apply-alumier-wrap');
    if (applyAlumier) applyAlumier.checked = true;
    if (applyAlumierWrap) applyAlumierWrap.style.display = 'block';
  }

  if (savedRegisterUrl && document.body.classList.contains('template--index')) {
    if (checkemail) registerAlumier();
    setTimeout(function() { window.location.href = savedRegisterUrl; }, 3000);
  }

  /* ----------------------------- Login redirect from /account ----------------------------- */
  var savedUrlLogin = localStorage.getItem('savedUrlLogin');
  if (document.body.classList.contains('template--account') && savedUrlLogin) {
    localStorage.removeItem('savedUrlLogin');
    window.location.href = savedUrlLogin;
  }

  /* ----------------------------- Run the main check ----------------------------- */
  checkAlumierEmail();

  /* ----------------------------- Expose hideButtons globally for cart-drawer.js ----------------------------- */
  window.alumierHideButtons = hideButtons;

  /* ----------------------------- Collection page card buttons (delayed for DOM render) ----------------------------- */
  setTimeout(function() { hideButtons(); }, 3000);

  /* Re-apply when product recommendations or other dynamic content loads AlumierMD cards */
  // (Only needed in the old flow - skip the body-wide observer entirely when the gate is off.)
  if (ALUMIER_ATC_GATE_ENABLED) {
    var _lastCardCount = 0;
    var _cardObserver = new MutationObserver(function() {
      var cards = document.querySelectorAll('.hide-detail-button');
      if (cards.length !== _lastCardCount) {
        _lastCardCount = cards.length;
        hideButtons();
      }
    });
    _cardObserver.observe(document.body, { childList: true, subtree: true });
  }
})

$(document).ready(function(){
  $('.info-slider').slick({
    infinite: true,
    speed: 300,
    slidesToShow: 4,
    arrows: true,
    dots: false,
    prevArrow:`<button type='button' class='slick-prev pull-left'><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"></path></svg></button>`,
    nextArrow: `<button type='button' class='slick-next pull-right'><svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"></path></svg></button>`,
    autoplay: true,
    autoplaySpeed: 2000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3
        }
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 580,
        settings: {
          slidesToShow: 1
        }
      }
      // You can unslick at a given breakpoint now by adding:
      // settings: "unslick"
      // instead of a settings object
    ]
  });
});

/* ============================================
   Recurpay PDP — Subscribe & Save toggle
   Injects a clickable toggle bar after the ATC button.
   The toggle controls collapse/expand of the recurpay widget
   via .recurpay--expanded class, coordinated with CSS transitions.
   ============================================ */
(function () {
  'use strict';

  /**
   * Attempt to read the product price from the page using several
   * common selectors used across Enterprise theme variants.
   * Returns a numeric value in the store's base currency unit (e.g. pounds),
   * or null if nothing usable is found.
   */
  function getProductPrice() {
    var selectors = [
      '.price__current .money',
      '.price-item--regular',
      '.product__price .price-item--regular',
      '.product-info__price .price-item--regular',
      '.price--regular .money',
      '[data-product-price]',
      '.price .money'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el) continue;
      // Strip currency symbols, commas and whitespace; keep digits and decimal point
      var raw = el.textContent.replace(/[^\d.]/g, '').trim();
      var parsed = parseFloat(raw);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return null;
  }

  /**
   * Format a numeric savings amount as a GBP string, e.g. "£1.25".
   */
  function formatSavings(amount) {
    return '\u00a3' + amount.toFixed(2);
  }

  /**
   * Build the toggle label text. If we can determine the price we include
   * the calculated saving; otherwise we show a plain CTA.
   */
  function buildToggleLabel() {
    var price = getProductPrice();
    if (price !== null) {
      var saving = price * 0.05;
      return 'Subscribe & Save 5% \u2014 Save ' + formatSavings(saving) + ' per order';
    }
    return 'Subscribe & Save 5%';
  }

  /**
   * Create the toggle DOM element.
   */
  function createToggleElement() {
    var toggle = document.createElement('div');
    toggle.className = 'recurpay-toggle';
    toggle.setAttribute('role', 'button');
    toggle.setAttribute('tabindex', '0');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.style.order = '2';

    var label = document.createElement('span');
    label.className = 'recurpay-toggle__label';
    label.textContent = buildToggleLabel();

    var chevron = document.createElement('span');
    chevron.className = 'recurpay-toggle__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    toggle.appendChild(label);
    toggle.appendChild(chevron);
    return toggle;
  }

  /**
   * Wire up a single recurpay widget with its toggle.
   */
  function initWidget(widget) {
    // On AlumierMD PDPs the toggle must never exist while the widget is gated off — creating it
    // would surface a "Subscribe & Save" bar to an unverified visitor. init() re-runs when the
    // gate opens, so the toggle is wired up the moment the widget legitimately becomes visible.
    if (!recurpayAllowed()) return;

    // Find the ATC container — walk up to the form then look for the sibling
    var atcContainer = widget.parentElement
      ? widget.parentElement.querySelector('.product-info__add-to-cart')
      : null;

    if (!atcContainer) return;

    // Avoid double-initialising. init() re-runs on the `alumier:state` event (see below), which
    // fires later for logged-in customers (async verification) than for guests — so a second
    // .recurpay-toggle only ever appeared when logged in. Checking just the immediate next
    // sibling broke as soon as anything else (e.g. cmr-mobile-nav.liquid's .cmr-jump anchor) got
    // inserted between the ATC container and the toggle on that second run — the sibling check
    // no longer saw the existing toggle and created another one. Search the whole container
    // instead so an intervening element can never defeat this guard.
    if (widget.parentElement && widget.parentElement.querySelector('.recurpay-toggle')) return;

    var toggle = createToggleElement();

    // Widget gets order: 3 via inline style; toggle is order: 2 (set above)
    widget.style.order = '3';

    // Insert toggle immediately after the ATC container in the DOM.
    // CSS flex order handles the visual position independently.
    atcContainer.insertAdjacentElement('afterend', toggle);

    // Start EXPANDED so the subscription options are visible on page load.
    // The toggle still collapses/expands on click; this only sets the initial state.
    widget.classList.add('recurpay--expanded');
    toggle.classList.add('expanded');
    toggle.setAttribute('aria-expanded', 'true');

    function handleToggle() {
      var isExpanded = widget.classList.contains('recurpay--expanded');
      if (isExpanded) {
        widget.classList.remove('recurpay--expanded');
        toggle.classList.remove('expanded');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        widget.classList.add('recurpay--expanded');
        toggle.classList.add('expanded');
        toggle.setAttribute('aria-expanded', 'true');
      }
    }

    toggle.addEventListener('click', handleToggle);

    // Keyboard support: Space and Enter activate the toggle
    toggle.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleToggle();
      }
    });
  }

  /**
   * True when a toggle may be created/revealed. Off AlumierMD PDPs this is always true, so the
   * existing behaviour on every other product is untouched.
   */
  function recurpayAllowed() {
    if (!document.body || !document.body.classList.contains('product-alumiermd')) return true;
    return window.__alumierRecurpay
      ? window.__alumierRecurpay.isAllowed()
      : window.__alumierRecurpayAllowed === true;
  }

  function init() {
    var widgets = document.querySelectorAll('.recurpay-pdp-widget');
    for (var i = 0; i < widgets.length; i++) {
      initWidget(widgets[i]);
    }
    // Any toggle we created is subject to the same gate as the widget.
    if (window.__alumierRecurpay) window.__alumierRecurpay.enforce();
  }

  // The gate can open after this block first runs (verification resolves asynchronously), so
  // re-attempt injection whenever the AlumierMD state changes.
  document.addEventListener('alumier:state', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already ready (e.g. script loaded deferred/async)
    init();
  }
}());

/* ============================================
   Loyalty Points Calculator
   Populates [data-loyalty-points] spans with
   calculated points based on selected variant price.
   Recalculates on variant change (pack size switch).
   1 point per £1 spent (price_cents / 100).
   ============================================ */
(function () {
  var variantPriceMap = {};

  function buildVariantMap() {
    var vp = document.querySelector('variant-picker');
    if (!vp) return;
    var script = vp.querySelector('script[type="application/json"]');
    if (!script) return;
    try {
      var data = JSON.parse(script.textContent);
      if (data && data.product && data.product.variants) {
        data.product.variants.forEach(function (v) {
          variantPriceMap[String(v.id)] = v.price;
        });
      }
    } catch (e) {}
  }

  function getCurrentPrice() {
    var input = document.querySelector('input[name="id"]');
    if (!input) return 0;
    var vid = String(input.value);
    if (variantPriceMap[vid]) return variantPriceMap[vid];
    // Fallback: read from data attribute
    var span = document.querySelector('[data-loyalty-points]');
    if (span) {
      var cents = parseInt(span.getAttribute('data-price-cents'), 10);
      if (!isNaN(cents)) return cents;
    }
    return 0;
  }

  function updateLoyaltyPoints() {
    var priceCents = getCurrentPrice();
    if (!priceCents || priceCents <= 0) return;
    var points = Math.floor(priceCents / 100);

    var spans = document.querySelectorAll('[data-loyalty-points]');
    for (var i = 0; i < spans.length; i++) {
      spans[i].textContent = points;
    }

    // Swell fallback
    var swellSpans = document.querySelectorAll('.swell-product-helper');
    for (var j = 0; j < swellSpans.length; j++) {
      swellSpans[j].textContent = points;
    }
  }

  function watchVariantChanges() {
    var input = document.querySelector('input[name="id"]');
    if (!input) return;

    // Watch for value changes via MutationObserver
    var observer = new MutationObserver(function () {
      updateLoyaltyPoints();
    });
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });

    // Also listen for change event
    input.addEventListener('change', updateLoyaltyPoints);

    // Watch the variant picker for clicks (radio/swatch based)
    var vp = document.querySelector('variant-picker, .variant-picker');
    if (vp) {
      vp.addEventListener('change', function () {
        setTimeout(updateLoyaltyPoints, 100);
      });
      vp.addEventListener('click', function () {
        setTimeout(updateLoyaltyPoints, 200);
      });
    }

    // Fallback: poll every 2s for first 30s in case input changes via JS
    var pollCount = 0;
    var lastVal = input.value;
    var pollTimer = setInterval(function () {
      pollCount++;
      if (pollCount > 15) { clearInterval(pollTimer); return; }
      if (input.value !== lastVal) {
        lastVal = input.value;
        updateLoyaltyPoints();
      }
    }, 2000);
  }

  function init() {
    buildVariantMap();
    setTimeout(function () {
      updateLoyaltyPoints();
      watchVariantChanges();
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());

/* ============================================
   Subscription Info Link — circled "i" icon
   Injects on PDP (.recurpay-pdp-widget) and
   cart drawer (.upgrade-subscription-cart).
   ============================================ */
(function () {
  var SUBSCRIPTION_PAGE = '/pages/subscriptions';
  var LINK_TEXT = 'How do subscriptions work?';
  var CHECK_INTERVAL = 2000;
  var MAX_ATTEMPTS = 15;
  var LINK_CLASS = 'subscription-info-link';

  function createCircledI() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.style.flexShrink = '0';

    var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    circle.setAttribute('stroke', 'currentColor');
    circle.setAttribute('stroke-width', '2');

    var line = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    line.setAttribute('d', 'M12 11v5');
    line.setAttribute('stroke', 'currentColor');
    line.setAttribute('stroke-width', '2.5');
    line.setAttribute('stroke-linecap', 'round');

    var dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', '12');
    dot.setAttribute('cy', '7.5');
    dot.setAttribute('r', '1.5');
    dot.setAttribute('fill', 'currentColor');

    svg.appendChild(circle);
    svg.appendChild(line);
    svg.appendChild(dot);
    return svg;
  }

  function createLink() {
    var link = document.createElement('a');
    link.href = SUBSCRIPTION_PAGE;
    link.className = LINK_CLASS;
    link.appendChild(createCircledI());
    link.appendChild(document.createTextNode(' ' + LINK_TEXT));
    return link;
  }

  /**
   * True when the PDP info link may be injected. Off AlumierMD PDPs this is always true, so every
   * other product — and every cart-drawer injection below — behaves exactly as before.
   */
  function recurpayAllowed() {
    if (!document.body || !document.body.classList.contains('product-alumiermd')) return true;
    return window.__alumierRecurpay
      ? window.__alumierRecurpay.isAllowed()
      : window.__alumierRecurpayAllowed === true;
  }

  function injectInfoLinks() {
    var injected = false;

    /* --- PDP: after the recurpay expanded widget (below subscribe & save) ---
       On AlumierMD PDPs this link is part of the subscribe UI, so it obeys the same verification
       gate as the widget and toggle: don't even create it until the customer is cleared to buy.
       This branch is PDP-only (drawer instances are excluded below), so gating here cannot affect
       the cart drawer. */
    var pdpWidget = document.querySelector('.recurpay-pdp-widget');
    if (pdpWidget && !pdpWidget.closest('.drawer--cart') && !pdpWidget.closest('cart-drawer') && recurpayAllowed()) {
      if (!pdpWidget.nextElementSibling || !pdpWidget.nextElementSibling.classList.contains(LINK_CLASS)) {
        if (!pdpWidget.parentElement || !pdpWidget.parentElement.querySelector('.' + LINK_CLASS)) {
          pdpWidget.insertAdjacentElement('afterend', createLink());
          injected = true;
        }
      }
    }

    /* --- Cart drawer: after .upgrade-subscription-cart widgets --- */
    var drawer = document.querySelector('.drawer--cart') || document.querySelector('cart-drawer');
    if (drawer) {
      var cartWidgets = drawer.querySelectorAll('.upgrade-subscription-cart');
      cartWidgets.forEach(function (widget) {
        if (widget.querySelector('.' + LINK_CLASS)) return;
        if (widget.nextElementSibling && widget.nextElementSibling.classList.contains(LINK_CLASS)) return;
        widget.appendChild(createLink());
        injected = true;
      });

      /* Fallback: top-level recurpay widgets in drawer */
      var recurpayWidgets = drawer.querySelectorAll('[class*="recurpay"]');
      recurpayWidgets.forEach(function (widget) {
        if (widget.closest('[class*="recurpay"]') !== widget) return;
        if (widget.querySelector('.' + LINK_CLASS)) return;
        if (widget.nextElementSibling && widget.nextElementSibling.classList.contains(LINK_CLASS)) return;
        widget.insertAdjacentElement('afterend', createLink());
        injected = true;
      });
    }

    // Anything just injected in PDP context is subject to the AlumierMD gate.
    if (injected && window.__alumierRecurpay) {
      window.__alumierRecurpay.observe();
      window.__alumierRecurpay.enforce();
    }

    return injected;
  }

  var attempts = 0;
  function tryInject() {
    attempts++;
    if (injectInfoLinks() || attempts >= MAX_ATTEMPTS) return;
    setTimeout(tryInject, CHECK_INTERVAL);
  }

  // Verification resolves asynchronously and the retry loop is capped, so re-attempt injection
  // whenever the AlumierMD state changes — that is the moment the gate may open.
  document.addEventListener('alumier:state', function () {
    attempts = 0;
    injectInfoLinks();
  });

  function startObserving() {
    setTimeout(tryInject, CHECK_INTERVAL);

    var observer = new MutationObserver(function () {
      setTimeout(function () {
        attempts = 0;
        tryInject();
      }, 1000);
    });

    var target = document.querySelector('.drawer--cart') || document.querySelector('cart-drawer') || document.body;
    observer.observe(target, { childList: true, subtree: true });

    /* Also observe the PDP product-info area for late-loading widgets */
    var pdpArea = document.querySelector('.product-info') || document.querySelector('[data-product-form]');
    if (pdpArea && pdpArea !== target) {
      observer.observe(pdpArea, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
}());